import { vec2 } from 'gl-matrix';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import {
    buildDoseDisplayView,
    DoseDisplayView,
} from '~/hooks/dose/doseDisplayView';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { useDoseDisplaySettings } from '~/hooks/dose/useDoseDisplaySettings';
import { useDoseDetailView } from '~/hooks/domain/useDoseDetailView';
import { selectedDoseRangeState } from '~/state/atoms/dose/doseAtoms';

export interface UseDoseResult {
    /** Display-enriched view of the resolved dose; undefined if none. */
    dose: DoseDisplayView | undefined;
    /** Returns false when the store rejected the value (nothing applied). */
    setNormalizationValue: (normalizationValueGy: number) => boolean;
    /** Returns false when the store rejected the value (nothing applied). */
    setDoseScale: (doseScale: number) => boolean;
    /** Live drag preview — atom + engine only, no persisted commit. */
    previewDoseRange: (selectedRange: vec2) => void;
    /** End-of-gesture commit — validates, persists to the UI store, previews. */
    commitDoseRange: (selectedRange: vec2) => boolean;
}

/**
 * Application-layer hook for the selected (or explicitly supplied) dose.
 *
 * The clinical fields come from {@link useDoseDetailView} (a domain
 * projection); the display settings — scale, normalization, colorwash range
 * — come from the per-dose UI store ({@link useDoseDisplaySettings}). The
 * two are merged into a {@link DoseDisplayView}. Display writes go to the UI
 * store, not the domain.
 *
 * Units: `normalizationValueGy` is Gy, `selectedRange` is normalized to
 * 0..1 of max dose, `doseScale` is dimensionless.
 *
 * @param overrideDoseSource Render a specific dose instead of the current
 *   selection (used by the export dialog to show per-item details).
 */
export default function useDose(
    overrideDoseSource?: DoseMatrix
): UseDoseResult {
    const selectedDose = useCurrentDose();
    const resolvedDose = overrideDoseSource ?? selectedDose;
    const detail = useDoseDetailView(resolvedDose?.id ?? null);
    const {
        settings,
        setDoseScale: storeSetDoseScale,
        setNormalizationValueGy: storeSetNormalization,
        setSelectedRange: storeSetSelectedRange,
    } = useDoseDisplaySettings(resolvedDose?.id ?? null);
    const setSelectedDoseRange = useSetRecoilState(selectedDoseRangeState);

    // Memoize the merged view so its identity is stable across renders when
    // neither the projection nor the display settings changed. Both inputs
    // are already stable references (useDoseDetailView is memoized; `settings`
    // is a store entry or the module-constant default). Without this, a fresh
    // object every render makes `dose` unstable, and consumers that use it as
    // an effect dependency (the DVH chart and dose-statistics table) fall
    // into a set-state → re-render → new `dose` → recompute loop.
    const dose = useMemo(
        () => (detail ? buildDoseDisplayView(detail, settings) : undefined),
        [detail, settings]
    );

    const currentSopUidRef = useRef<string | undefined>(undefined);
    // Latest-value ref: the restore effect below must read the committed
    // settings but must NOT re-run when they change. Listing them as
    // dependencies would be misleading — every such run exits at the sopUid
    // guard — and re-running on a settings change would fight the live drag,
    // which deliberately drives the engine without touching the store.
    const settingsRef = useRef(settings);
    settingsRef.current = settings;

    useEffect(() => {
        if (!resolvedDose) {
            currentSopUidRef.current = undefined;
            return;
        }

        const sopUid = resolvedDose.dicomUid;
        if (sopUid === currentSopUidRef.current) {
            return;
        }
        currentSopUidRef.current = sopUid;

        // Restore the per-dose colorwash range into the live slider atom
        // after switching; the display bridge carries the atom to every
        // drawn dose. The committed range lives in the UI display store,
        // keyed by DoseId. Clone: the store entry is shared by every reader
        // of this dose, so handing the same buffer to the slider atom would
        // let a reader mutate the committed window for all of them.
        setSelectedDoseRange(vec2.clone(settingsRef.current.selectedRange));
    }, [resolvedDose, setSelectedDoseRange]);

    const setNormalizationValue = useCallback(
        (normalizationValueGy: number): boolean =>
            storeSetNormalization(normalizationValueGy),
        [storeSetNormalization]
    );

    const setDoseScale = useCallback(
        (nextScale: number): boolean => storeSetDoseScale(nextScale),
        [storeSetDoseScale]
    );

    /**
     * Live preview of the colorwash window during a slider drag.
     *
     * Updates the slider atom only, which the display bridge carries to
     * every drawn dose — deliberately NO persisted write. This runs on
     * every drag frame; committing per frame would rewrite the display
     * store every frame. Call {@link commitDoseRange} when the gesture ends.
     */
    const previewDoseRange = useCallback(
        (selectedRange: vec2) => {
            setSelectedDoseRange(selectedRange);
        },
        [setSelectedDoseRange]
    );

    /**
     * Commits the colorwash window to the per-dose UI store so it survives a
     * dose switch. Called once at the end of a drag (`onChangeCommitted`) or
     * immediately for a typed value, never per frame. Validation lives in
     * the store setter, which rejects an out-of-range window; on rejection
     * the preview is skipped so the engine is not left showing a window the
     * store never accepted, and `false` is returned so the caller can say so.
     */
    const commitDoseRange = useCallback(
        (selectedRange: vec2): boolean => {
            if (!storeSetSelectedRange(selectedRange)) {
                return false;
            }
            previewDoseRange(selectedRange);
            return true;
        },
        [storeSetSelectedRange, previewDoseRange]
    );

    return {
        dose,
        setNormalizationValue,
        setDoseScale,
        previewDoseRange,
        commitDoseRange,
    };
}
